<script lang="ts">
	import type { Numeric, ScaleContinuousNumeric } from 'd3';
	import type { Snippet } from 'svelte';

	interface Props {
		/**
		 * Which side of the axis you want the ticks and labels to appear on
		 */
		position: 'top' | 'bottom' | 'both';
		/**
		 * The set of ticks
		 */
		ticks: Numeric[];
		/**
		 * The scale
		 */
		scale: ScaleContinuousNumeric<Numeric, Numeric>;
		/**
		 * The units for positioning the ticks and labels.
		 * @default "px"
		 */
		units?: 'px' | '%';
		/**
		 * A snippet for rendering the tick
		 */
		tick?: Snippet<[Numeric, number, Numeric[]]>;
		/**
		 * A snippet for rendering the label
		 */
		label?: Snippet<[Numeric, number, Numeric[]]>;
		/**
		 * A snippet for rendering the line
		 */
		line?: Snippet<[Numeric, number, Numeric[]]>;
	}

	let { position, ticks, scale, units, tick, label, line }: Props = $props();
</script>

{#snippet renderTicks(pos: 'top' | 'bottom')}
	{#each ticks as t, idx (t)}
		{#if tick}
			<div
				class={['g-tick', pos === 'top' && 'g-top', pos === 'bottom' && 'g-bottom']}
				style:left="{scale(t)}{units}"
			>
				{@render tick(t, idx, ticks)}
			</div>
		{/if}
		{#if label}
			<div
				class={['g-label', pos === 'top' && 'g-top', pos === 'bottom' && 'g-bottom']}
				style:left="{scale(t)}{units}"
			>
				{@render label(t, idx, ticks)}
			</div>
		{/if}
	{/each}
{/snippet}

<div class="g-axis">
	{#if position === 'top' || position === 'both'}
		{@render renderTicks('top')}
	{/if}
	{#if position === 'bottom' || position === 'both'}
		{@render renderTicks('bottom')}
	{/if}
	{#if line}
		{#each ticks as t, idx (t)}
			<div class="g-line" style:left="{scale(t)}{units}">
				{@render line(t, idx, ticks)}
			</div>
		{/each}
	{/if}
</div>

<style>
	.g-tick,
	.g-label,
	.g-line {
		position: absolute;
	}

	.g-tick.g-top,
	.g-label.g-top,
	.g-line {
		top: 0;
	}

	.g-tick.g-bottom,
	.g-label.g-bottom {
		bottom: 0;
	}

	.g-line {
		height: 100%;
	}

	.g-label {
		line-height: 1;
		transform: translateX(-50%);
	}
</style>
